import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  limit,
} from "firebase/firestore";
import { getClientFirestore } from "@/api/firebase";
import { useAuthStore } from "@/stores/auth-store";

export interface SyncLogEntry {
  id: string;
  timestamp: { seconds: number; nanoseconds: number } | null;
  level: "info" | "warn" | "error";
  message: string;
  endpoint?: string;
  method?: string;
  statusCode?: number;
  recordCount?: number;
  durationMs?: number;
  error?: string;
}

export function useSyncConsole(connectionId: string | null) {
  const orgId = useAuthStore((s) => s.orgId);
  const [entries, setEntries] = useState<SyncLogEntry[]>([]);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    if (!orgId || !connectionId) {
      setEntries([]);
      setIsListening(false);
      return;
    }

    const clientDb = getClientFirestore(orgId);
    const q = query(
      collection(clientDb, "edcConnections", connectionId, "syncLogs"),
      orderBy("timestamp", "asc"),
      limit(500),
    );

    setIsListening(true);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const logs: SyncLogEntry[] = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            timestamp: d.timestamp ?? null,
            level: d.level ?? "info",
            message: d.message ?? "",
            endpoint: d.endpoint,
            method: d.method,
            statusCode: d.statusCode,
            recordCount: d.recordCount,
            durationMs: d.durationMs,
            error: d.error,
          };
        });
        setEntries(logs);
      },
      (error) => {
        console.error("Sync console listener error:", error);
        setIsListening(false);
      },
    );

    return () => {
      unsubscribe();
      setIsListening(false);
    };
  }, [orgId, connectionId]);

  return { entries, isListening };
}
